import * as THREE from 'three';

export function probeUniforms() {
  const slot = () => ({position:new THREE.Vector3(),min:new THREE.Vector3(),max:new THREE.Vector3(),blend:1,intensity:1});
  return {
    owCapture:{value:false},owAtlas:{value:new THREE.Vector3()},owPositions:{value:[slot().position,slot().position]},
    owMin:{value:[slot().min,slot().min]},owMax:{value:[slot().max,slot().max]},owBlend:{value:new THREE.Vector2(1,1)},
    owIntensity:{value:new THREE.Vector2(1,1)},owReflection:{value:new THREE.Vector2()},owIndirect:{value:new THREE.Vector2()},
    owMap0:{value:null as THREE.Texture|null},owMap1:{value:null as THREE.Texture|null},
    owSH0:{value:Array.from({length:9},()=>new THREE.Vector3())},owSH1:{value:Array.from({length:9},()=>new THREE.Vector3())},
  };
}

// PMREM atlases need their own dimensions: room probes and the sky can have
// different resolutions. Reuse the pinned Three sampler with independent names.
const cube=THREE.ShaderChunk.cube_uv_reflection_fragment;
function shaderFunction(signature:string):string {
  const start=cube.indexOf(signature),body=cube.indexOf('{',start);let depth=0;
  if(start<0||body<0)throw new Error('Three PMREM sampler interface changed');
  for(let i=body;i<cube.length;i++){if(cube[i]==='{')depth++;if(cube[i]==='}'&&--depth===0)return cube.slice(start,i+1)+'\n';}
  throw new Error('Incomplete Three PMREM sampler');
}
const sampler=(shaderFunction('vec3 bilinearCubeUV(')+shaderFunction('vec4 textureCubeUV(')).replaceAll('bilinearCubeUV','owBilinearCubeUV').replaceAll('textureCubeUV','owTextureCubeUV')
  .replaceAll('CUBEUV_MAX_MIP','owAtlas.z').replaceAll('CUBEUV_TEXEL_WIDTH','owAtlas.x').replaceAll('CUBEUV_TEXEL_HEIGHT','owAtlas.y');
const helpers=/* glsl */`
  uniform bool owCapture;
  varying vec3 owWorldPosition;
  #ifdef ENVMAP_TYPE_CUBE_UV
  uniform vec3 owAtlas;
  uniform vec3 owPositions[2];
  uniform vec3 owMin[2];
  uniform vec3 owMax[2];
  uniform vec2 owBlend,owIntensity,owReflection,owIndirect;
  uniform sampler2D owMap0,owMap1;
  uniform vec3 owSH0[9];
  uniform vec3 owSH1[9];
  vec3 shGetIrradianceAt(in vec3 normal,in vec3 shCoefficients[9]);
  ${sampler}
  float owWeight(int i) {
    vec3 edge=min(owWorldPosition-owMin[i],owMax[i]-owWorldPosition);
    return smoothstep(0.,owBlend[i],min(edge.x,min(edge.y,edge.z)));
  }
  vec3 owBoxDirection(vec3 direction,int i,float roughness) {
    vec3 safe=sign(direction)*max(abs(direction),vec3(.00001));
    safe+=vec3(equal(safe,vec3(0.)))*.00001;
    vec3 farHit=max((owMax[i]-owWorldPosition)/safe,(owMin[i]-owWorldPosition)/safe);
    float distance=min(farHit.x,min(farHit.y,farHit.z));
    vec3 projected=normalize(owWorldPosition+direction*max(0.,distance)-owPositions[i]);
    return normalize(mix(projected,direction,roughness*roughness));
  }
  vec3 owRadiance(vec3 global,vec3 direction,float roughness) {
    if(owCapture||owReflection.x+owReflection.y==0.) return global;
    vec2 weights=vec2(owWeight(0),owWeight(1))*owReflection;
    float total=weights.x+weights.y;
    if(total<=.00001) return global;
    vec3 local=vec3(0.);
    if(weights.x>0.) local+=owTextureCubeUV(owMap0,owBoxDirection(direction,0,roughness),roughness).rgb*owIntensity.x*weights.x;
    if(weights.y>0.) local+=owTextureCubeUV(owMap1,owBoxDirection(direction,1,roughness),roughness).rgb*owIntensity.y*weights.y;
    return mix(global,local/total,min(1.,total));
  }
  vec3 owIrradiance(vec3 global,vec3 normal) {
    if(owCapture||owIndirect.x+owIndirect.y==0.) return global;
    vec2 weights=vec2(owWeight(0),owWeight(1))*step(vec2(.5),owIndirect);
    float total=weights.x+weights.y;
    if(total<=.00001) return global;
    vec3 local=vec3(0.);
    if(weights.x>0.) local+=(owIndirect.x>1.5?max(vec3(0.),shGetIrradianceAt(normal,owSH0)):max(vec3(0.),PI*owTextureCubeUV(owMap0,normal,1.).rgb-global))*owIntensity.x*weights.x;
    if(weights.y>0.) local+=(owIndirect.y>1.5?max(vec3(0.),shGetIrradianceAt(normal,owSH1)):max(vec3(0.),PI*owTextureCubeUV(owMap1,normal,1.).rgb-global))*owIntensity.y*weights.y;
    return global+local/total*min(1.,total);
  }
  #endif
`;

export function patchProbeShader(shader: THREE.WebGLProgramParametersWithUniforms,uniforms: ReturnType<typeof probeUniforms>): void {
  Object.assign(shader.uniforms,uniforms);
  shader.vertexShader='varying vec3 owWorldPosition;\n'+shader.vertexShader;
  shader.vertexShader=shader.vertexShader.replace('#include <worldpos_vertex>',`#include <worldpos_vertex>
    vec4 owPosition=vec4(transformed,1.);
    #ifdef USE_BATCHING
      owPosition=batchingMatrix*owPosition;
    #endif
    #ifdef USE_INSTANCING
      owPosition=instanceMatrix*owPosition;
    #endif
    owWorldPosition=(modelMatrix*owPosition).xyz;`);
  const physical=THREE.ShaderChunk.envmap_physical_pars_fragment
    .replace('return PI * envMapColor.rgb * envMapIntensity;','return owIrradiance(PI * envMapColor.rgb * envMapIntensity, worldNormal);')
    .replace('return envMapColor.rgb * envMapIntensity;','return owRadiance(envMapColor.rgb * envMapIntensity, reflectVec, roughness);');
  shader.fragmentShader=shader.fragmentShader.replace('#include <envmap_physical_pars_fragment>',helpers+physical)
    .replace('#include <tonemapping_fragment>','if(owCapture) gl_FragColor.rgb=clamp(gl_FragColor.rgb,vec3(0.),vec3(64.));\n#include <tonemapping_fragment>');
}
